import React, { useEffect, useState } from "react";
import axios from "axios";
import { Bar } from "react-chartjs-2";
import {
	Chart as ChartJS,
	BarElement,
	CategoryScale,
	LinearScale,
	Legend,
	Tooltip,
} from "chart.js";
import ParallexSpinner from "../components/ParallexSpinner";

ChartJS.register(BarElement, CategoryScale, LinearScale, Legend, Tooltip);

const statusColors = {
	DELIVRD: "#4caf50",
	EXPIRED: "#ff9800",
	UNDELIV: "#f44336",
	PENDING: "#9e9e9e",
};

const Dashboard = () => {
	const [rawStats, setRawStats] = useState({});
	const [loading, setLoading] = useState(true);
	const [statusLabels, setStatusLabels] = useState([]);

	useEffect(() => {
		const fetchData = async () => {
			try {
				const token = localStorage.getItem("parallextoken");

				if (!token) {
					console.error("No authentication token found.");
					return;
				}

				const { data } = await axios.get(
					"https://parallexsms.approot.ng/delivery_stats.php",
					{
						headers: {
							Authorization: `Bearer ${token}`,
						},
					}
				);

				if (!data.success) return;

				const transformed = {};
				const labelsSet = new Set();

				const norm = (raw) => {
					if (raw === "0") return "PENDING";
					if (raw.toUpperCase() === "EXPIRD") return "EXPIRED";
					return raw;
				};

				for (const net in data.data) {
					transformed[net] = {};
					for (const rawStatus in data.data[net]) {
						const label = norm(rawStatus);
						const count = data.data[net][rawStatus];
						transformed[net][label] = (transformed[net][label] || 0) + count;
						labelsSet.add(label);
					}
				}

				setRawStats(transformed);
				setStatusLabels(Array.from(labelsSet));
			} catch (err) {
				console.error("Failed to load stats:", err);
			} finally {
				setLoading(false);
			}
		};

		fetchData();
	}, []);

	const networks = Object.keys(rawStats);

	// Compute per-status totals and grand total
	const totalsPerStatus = {};
	let grandTotal = 0;
	networks.forEach((network) => {
		const stats = rawStats[network];
		for (const status of statusLabels) {
			const count = stats[status] || 0;
			totalsPerStatus[status] = (totalsPerStatus[status] || 0) + count;
			grandTotal += count;
		}
	});

	const barChartData = {
		labels: networks,
		datasets: statusLabels.map((status) => ({
			label: status,
			data: networks.map((network) => rawStats[network][status] || 0),
			backgroundColor: statusColors[status],
		})),
	};

	const barChartOptions = {
		responsive: true,
		plugins: {
			legend: { position: "top" },
			tooltip: { mode: "index", intersect: false },
		},
		scales: {
			x: { stacked: true },
			y: { stacked: true, beginAtZero: true },
		},
	};

	return (
		<div className='p-6'>
			<h1 className='text-2xl font-bold text-primary mb-4'>
				SMS Delivery Overview
			</h1>

			{loading ? (
  <div className="flex h-96 items-center justify-center">
    <ParallexSpinner label="Fetching SMS stats..." size="lg" />
  </div>
) : (
				<>
					{/* Delivery Stats Table */}
					<div className='overflow-x-auto mb-6'>
						<table className='min-w-full text-sm border rounded shadow bg-white'>
							<thead className='bg-gray-200 text-left'>
								<tr>
									<th className='p-3'>Network</th>
									{statusLabels.map((label) => (
										<th className='p-3' key={label}>
											{label}
										</th>
									))}
									<th className='p-3'>Total</th>
								</tr>
							</thead>
							<tbody>
								{networks.map((network) => {
									const stats = rawStats[network];
									const total = Object.values(stats).reduce(
										(acc, val) => acc + val,
										0
									);

									return (
										<tr key={network} className='border-t'>
											<td className='p-3 font-semibold'>{network}</td>
											{statusLabels.map((status) => {
												const count = stats[status] || 0;
												const percent = total
													? ((count / total) * 100).toFixed(1)
													: 0;
												return (
													<td className='p-3' key={status}>
														<span className='font-medium'>
															{count.toLocaleString()}
														</span>
														<span className='text-xs text-gray-500 ml-1'>
															({percent}%)
														</span>
													</td>
												);
											})}
											<td className='p-3 font-bold text-blue-700'>
												{total.toLocaleString()}
											</td>
										</tr>
									);
								})}

								{/* Totals Row */}
								<tr className='bg-gray-100 border-t-2 font-semibold'>
									<td className='p-3'>Total</td>
									{statusLabels.map((status) => (
										<td className='p-3' key={status}>
											{totalsPerStatus[status]?.toLocaleString() || 0}
										</td>
									))}
									<td className='p-3 text-blue-700 font-bold'>
										{grandTotal.toLocaleString()}
									</td>
								</tr>
							</tbody>
						</table>
					</div>

					{/* Chart */}
					<div className='bg-white p-4 rounded shadow'>
						<h2 className='text-lg font-semibold mb-2'>
							Delivery Status per Network
						</h2>
						<Bar data={barChartData} options={barChartOptions} height={300} />
					</div>
				</>
			)}
		</div>
	);
};

export default Dashboard;
